import { ensureKeyboardInstructions } from './announcer'
import type { DndScope, Gesture } from './scope'
import type { DndEdge, DndEngineHandlers, DndEngineHover, DndEngineOver } from './types'

// Kits register once at module scope; element handlers live in a WeakMap so
// an unmounted droppable can never leak.
const kits = new Map<string, DndEngineHandlers>()
const elementHandlers = new WeakMap<Element, DndEngineHandlers>()
let nextKitNumber = 1

/** Registers a kit and returns its id. Call once per kit, at module scope. */
export function registerDndKit(handlers: DndEngineHandlers): string {
    const kitId = `astra-dnd-${nextKitNumber}`
    nextKitNumber += 1
    kits.set(kitId, handlers)
    ensureKeyboardInstructions()
    return kitId
}

/**
 * Attaches drop handlers to a droppable or zone element. Returns the cleanup
 * for a React 19 ref callback. The cleanup only removes its own entry, so a
 * newer registration on the same element survives an older cleanup.
 */
export function registerDndElementHandlers(element: Element, handlers: DndEngineHandlers): () => void {
    elementHandlers.set(element, handlers)
    return () => {
        if (elementHandlers.get(element) === handlers) elementHandlers.delete(element)
    }
}

function zoneHandlers(scope: DndScope): DndEngineHandlers | undefined {
    return scope.zone === null ? undefined : elementHandlers.get(scope.zone)
}

// A target without any onDrop in its layers would light up and then swallow
// the drop, so it reads as invalid instead.
export function isDropValid(gesture: Gesture, element: HTMLElement, dropId: string | undefined): boolean {
    const own = elementHandlers.get(element)
    const zone = zoneHandlers(gesture.scope)
    const kit = kits.get(gesture.scope.kitId)
    if ((own?.onDrop ?? zone?.onDrop ?? kit?.onDrop) === undefined) return false
    const canDrop = own?.canDrop ?? zone?.canDrop ?? kit?.canDrop
    return canDrop?.({ payload: gesture.payload, dropId, dropElement: element }) ?? true
}

export function emitDrop(gesture: Gesture, element: HTMLElement, dropId: string | undefined, edge: DndEdge | null): void {
    const onDrop = elementHandlers.get(element)?.onDrop ?? zoneHandlers(gesture.scope)?.onDrop ?? kits.get(gesture.scope.kitId)?.onDrop
    onDrop?.({ payload: gesture.payload, dropId, dropElement: element, edge })
}

export function emitTargetChange(gesture: Gesture, over: DndEngineOver | null): void {
    const onTargetChange = zoneHandlers(gesture.scope)?.onTargetChange ?? kits.get(gesture.scope.kitId)?.onTargetChange
    onTargetChange?.({ payload: gesture.payload, over })
}

export function emitHoverChange(element: Element, hover: DndEngineHover | null): void {
    elementHandlers.get(element)?.onHoverChange?.(hover)
}
